/* mootlu-product.js — PDP variant picker, image gallery, related products. Vanilla.
 *
 * Performance: avoid layout reads (offsetLeft/offsetWidth) on scroll; use
 * IntersectionObserver + index-based scrollTo. Batch DOM writes in rAF.
 */
(function () {
  'use strict';

  var DATA = window.MOOTLU_DATA || {};
  var T = DATA.t || {};
  var mobileMq = typeof window.matchMedia === 'function'
    ? window.matchMedia('(max-width: 768px)')
    : null;
  var slideObserver = null;
  var galleryDotsBuilt = false;
  var mainImageSwapToken = 0;

  function t(key, fallback) {
    var v = T[key];
    return (typeof v === 'string' && v.length) ? v : (fallback || '');
  }

  function galleryDotLabel(index) {
    return t('gallery_dot_label', 'Image {{ index }}').replace(
      /\{\{\s*index\s*\}\}/g,
      String(index)
    );
  }

  function resolveLocale(raw) {
    var l = String(raw || 'tr-TR').replace(/_/g, '-');
    if (l === 'tr' || l.toLowerCase().indexOf('tr-') === 0) return 'tr-TR';
    if (l === 'de' || l.toLowerCase().indexOf('de-') === 0) return 'de-DE';
    if (l === 'en' || l.toLowerCase().indexOf('en-') === 0) return 'en-GB';
    return l;
  }

  /** TRY → 1.970,10₺ (Turkish separators, suffix). */
  function formatMoneyCents(cents) {
    var currency = (DATA.currencyCode || DATA.currency || 'TRY').toUpperCase();
    var value = Number(cents) / 100;
    if (currency === 'TRY') {
      try {
        return (
          new Intl.NumberFormat('tr-TR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(value) + '₺'
        );
      } catch (e) {
        return value.toFixed(2).replace('.', ',') + '₺';
      }
    }
    try {
      return new Intl.NumberFormat(resolveLocale(DATA.locale), {
        style: 'currency',
        currency: currency,
        currencyDisplay: 'narrowSymbol',
      }).format(value);
    } catch (e2) {
      return value.toFixed(2) + ' ' + currency;
    }
  }

  function cartBasePercent() {
    var promos = DATA.cartPromotions || {};
    var pct = Number(promos.basePercent);
    if (!Number.isFinite(pct) || pct <= 0) return 15;
    return pct;
  }

  function formatMoneySuffixCents(cents) {
    return formatMoneyCents(cents);
  }

  function formatSavingsSuffixCents(cents) {
    return Math.round(Number(cents) / 100) + '₺';
  }

  function discountedCartPriceCents(priceCents) {
    var pct = cartBasePercent();
    return Math.round(priceCents * (100 - pct) / 100);
  }

  function updateLowStock(variant) {
    var el = document.querySelector('[data-low-stock]');
    if (!el) return;
    var textEl = el.querySelector('[data-low-stock-text]');
    var threshold = parseInt(el.getAttribute('data-low-stock-threshold'), 10) || 0;
    var qty = variant && variant.inventory_quantity;
    var managed = variant && variant.inventory_managed;
    if (threshold > 0 && managed && typeof qty === 'number' && qty > 0 && qty <= threshold) {
      if (textEl) textEl.textContent = t('low_stock', 'Son __N__ adet!').replace('__N__', qty);
      el.removeAttribute('hidden');
    } else {
      el.setAttribute('hidden', '');
    }
  }

  function updatePdpAdvantagesPrice(priceCents) {
    var advPriceEl = document.querySelector('[data-pdp-advantages-discounted]');
    var advSavingsEl = document.querySelector('[data-pdp-advantages-savings]');
    var installmentEl = document.querySelector('[data-pdp-installment]');
    if (priceCents == null) return;
    var discounted = discountedCartPriceCents(priceCents);
    if (advPriceEl) {
      advPriceEl.textContent = formatMoneySuffixCents(discounted);
    }
    if (advSavingsEl) {
      var savings = priceCents - discounted;
      advSavingsEl.textContent =
        '(' +
        formatSavingsSuffixCents(savings) +
        ' ' +
        t('advantageous_savings_label', 'Tasarruf') +
        ')';
    }
    if (installmentEl) {
      installmentEl.textContent = formatMoneySuffixCents(Math.round(discounted / 3));
    }
  }

  function isMobileGallery() {
    if (mobileMq) return mobileMq.matches;
    return window.innerWidth <= 768;
  }

  function galleryHasVariants() {
    var gallery = document.querySelector('[data-product-gallery]');
    return gallery && gallery.getAttribute('data-has-variants') === 'true';
  }

  function variantIdsForSlide(slide) {
    var csv = slide.getAttribute('data-variant-ids') || '';
    if (!csv) return [];
    return csv.split(',').map(function (id) { return id.trim(); }).filter(Boolean);
  }

  function slideVisibleForVariant(slide, variant) {
    if (!galleryHasVariants()) return true;

    if (
      variant &&
      variant.gallery_source === 'metafield' &&
      variant.gallery_image_ids &&
      variant.gallery_image_ids.length
    ) {
      var imageId = slide.getAttribute('data-image-id');
      for (var g = 0; g < variant.gallery_image_ids.length; g++) {
        if (String(variant.gallery_image_ids[g]) === String(imageId)) return true;
      }
      return false;
    }

    var vidStr = String(variant.id);
    var ids = variantIdsForSlide(slide);
    if (!ids.length) return false;
    return ids.indexOf(vidStr) !== -1;
  }

  function preferredSlideForVariant(variant) {
    if (!variant) return null;

    if (
      variant.gallery_source === 'metafield' &&
      variant.gallery_image_ids &&
      variant.gallery_image_ids.length
    ) {
      for (var g = 0; g < variant.gallery_image_ids.length; g++) {
        var byGallery = document.querySelector(
          '[data-thumb][data-image-id="' + variant.gallery_image_ids[g] + '"]:not([hidden])'
        );
        if (byGallery) return byGallery;
      }
      return null;
    }

    if (variant.featured_image_id) {
      return document.querySelector(
        '[data-thumb][data-image-id="' + variant.featured_image_id + '"]:not([hidden])'
      );
    }

    return null;
  }

  var PDP_MAIN_SIZES = '(min-width: 769px) min(50vw, 720px), 100vw';

  function normalizeImageUrl(url) {
    if (!url) return '';
    if (url.indexOf('//') === 0) return 'https:' + url;
    return url;
  }

  function imageUrlKey(url) {
    if (!url) return '';
    try {
      return new URL(normalizeImageUrl(url), window.location.origin).pathname;
    } catch (e) {
      return url;
    }
  }

  function sameImageUrl(a, b) {
    if (!a || !b) return false;
    return imageUrlKey(a) === imageUrlKey(b);
  }

  function setActiveSlide(slide) {
    var thumbs = document.querySelectorAll('[data-thumb]');
    for (var i = 0; i < thumbs.length; i++) {
      thumbs[i].classList.remove('is-active');
    }
    if (slide) slide.classList.add('is-active');
  }

  function applyMainImageSources(main, src, srcset) {
    var sizes = main.getAttribute('data-main-sizes') || PDP_MAIN_SIZES;
    // Browsers prefer srcset over src; stale srcset kept the first hero image visible.
    if (srcset) {
      main.srcset = srcset;
      main.sizes = sizes;
    } else {
      main.removeAttribute('srcset');
      main.removeAttribute('sizes');
    }
    main.src = src;
  }

  function setMainFromSlide(slide) {
    var main = document.querySelector('[data-main-image]');
    if (!main || !slide) return;
    var src = normalizeImageUrl(slide.getAttribute('data-image-src'));
    if (!src) return;
    var srcset = slide.getAttribute('data-image-srcset') || '';
    var current = main.currentSrc || main.getAttribute('src') || main.src;
    if (sameImageUrl(current, src) && (!srcset || main.srcset === srcset)) {
      main.style.opacity = '1';
      return;
    }

    var token = ++mainImageSwapToken;
    main.style.opacity = '0';

    var preload = new Image();
    preload.onload = function () {
      if (token !== mainImageSwapToken) return;
      main.loading = 'lazy';
      main.removeAttribute('fetchpriority');
      applyMainImageSources(main, src, srcset);
      if (typeof preload.decode === 'function') {
        preload.decode().then(function () {
          if (token !== mainImageSwapToken) return;
          main.style.opacity = '1';
        }).catch(function () {
          if (token !== mainImageSwapToken) return;
          main.style.opacity = '1';
        });
      } else {
        main.style.opacity = '1';
      }
    };
    preload.onerror = function () {
      if (token !== mainImageSwapToken) return;
      applyMainImageSources(main, src, srcset);
      main.style.opacity = '1';
    };
    preload.src = src;
  }

  function notifyGalleryImage(slide) {
    if (!slide) return;
    setMainFromSlide(slide);
    var src = normalizeImageUrl(slide.getAttribute('data-image-src'));
    if (!src) return;
    try {
      document.dispatchEvent(
        new CustomEvent('mootlu:gallery-active', { detail: { imageUrl: src } })
      );
    } catch (e) {}
    var tryonBtn = document.getElementById('mutlu-tryon-btn');
    if (tryonBtn) tryonBtn.setAttribute('data-product-image', src);
    var tryonImg = document.getElementById('mutlu-tryon-product-img');
    if (tryonImg && tryonImg.src !== src) {
      tryonImg.src = src;
      tryonImg.classList.remove('loaded');
    }
  }

  function visibleSlides() {
    return Array.prototype.filter.call(
      document.querySelectorAll('[data-thumb]'),
      function (s) {
        return !s.hasAttribute('hidden');
      }
    );
  }

  function buildGalleryDots() {
    var dotsWrap = document.querySelector('[data-pdp-dots]');
    if (!dotsWrap || !isMobileGallery() || galleryDotsBuilt) return;
    var slides = visibleSlides();
    var frag = document.createDocumentFragment();
    slides.forEach(function (slide, i) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className =
        'pdp-gallery__dot' + (slide.classList.contains('is-active') ? ' is-active' : '');
      dot.setAttribute('aria-label', galleryDotLabel(i + 1));
      if (slide.classList.contains('is-active')) {
        dot.setAttribute('aria-current', 'true');
      }
      dot.dataset.slideIndex = String(i);
      dot.addEventListener('click', function () {
        scrollToGallerySlide(slide);
      });
      frag.appendChild(dot);
    });
    dotsWrap.appendChild(frag);
    galleryDotsBuilt = true;
  }

  function resetGalleryDots() {
    var dotsWrap = document.querySelector('[data-pdp-dots]');
    if (dotsWrap) dotsWrap.innerHTML = '';
    galleryDotsBuilt = false;
    buildGalleryDots();
  }

  function updateGalleryChrome(activeSlide) {
    var slides = visibleSlides();
    if (!slides.length) return;
    var idx = slides.indexOf(activeSlide);
    if (idx < 0) idx = 0;

    var prevBtns = document.querySelectorAll('[data-pdp-prev]');
    var nextBtns = document.querySelectorAll('[data-pdp-next]');
    for (var p = 0; p < prevBtns.length; p++) {
      prevBtns[p].disabled = idx <= 0;
    }
    for (var n = 0; n < nextBtns.length; n++) {
      nextBtns[n].disabled = idx >= slides.length - 1;
    }

    if (!isMobileGallery()) return;

    var dots = document.querySelectorAll('.pdp-gallery__dot');
    for (var d = 0; d < dots.length; d++) {
      dots[d].classList.toggle('is-active', d === idx);
      if (d === idx) {
        dots[d].setAttribute('aria-current', 'true');
      } else {
        dots[d].removeAttribute('aria-current');
      }
    }
  }

  function disconnectSlideObserver() {
    if (slideObserver) {
      slideObserver.disconnect();
      slideObserver = null;
    }
  }

  function setupSlideObserver() {
    var viewport = document.querySelector('[data-pdp-scroll]');
    if (!viewport || !isMobileGallery()) return;

    disconnectSlideObserver();
    var vis = visibleSlides();
    if (!vis.length) return;

    slideObserver = new IntersectionObserver(
      function (entries) {
        var best = null;
        var bestRatio = 0;
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          if (entry.intersectionRatio > bestRatio) {
            bestRatio = entry.intersectionRatio;
            best = entry.target;
          }
        }
        if (!best || bestRatio < 0.45) return;
        requestAnimationFrame(function () {
          setActiveSlide(best);
          notifyGalleryImage(best);
          updateGalleryChrome(best);
        });
      },
      { root: viewport, threshold: [0.45, 0.65, 0.85] }
    );

    for (var j = 0; j < vis.length; j++) {
      slideObserver.observe(vis[j]);
    }
  }

  function goToGallerySlide(slide) {
    if (!slide) return;
    if (isMobileGallery()) {
      scrollToGallerySlide(slide);
      return;
    }
    setActiveSlide(slide);
    notifyGalleryImage(slide);
    updateGalleryChrome(slide);
  }

  function scrollToGallerySlide(slide) {
    if (!slide) return;
    if (isMobileGallery()) {
      var viewport = document.querySelector('[data-pdp-scroll]');
      var slides = visibleSlides();
      var idx = slides.indexOf(slide);
      if (viewport && idx >= 0) {
        var step = viewport.clientWidth;
        viewport.scrollTo({ left: idx * step, behavior: 'smooth' });
      }
    }
    setActiveSlide(slide);
    notifyGalleryImage(slide);
    updateGalleryChrome(slide);
  }

  function filterThumbsByVariant(variant) {
    var slides = document.querySelectorAll('[data-thumb]');
    if (!slides.length) return;

    requestAnimationFrame(function () {
      var firstVisible = null;
      var visibleFlags = [];
      for (var i = 0; i < slides.length; i++) {
        visibleFlags[i] = slideVisibleForVariant(slides[i], variant);
        if (visibleFlags[i] && !firstVisible) firstVisible = slides[i];
      }
      if (!firstVisible) return;
      for (var j = 0; j < slides.length; j++) {
        if (visibleFlags[j]) slides[j].removeAttribute('hidden');
        else slides[j].setAttribute('hidden', '');
      }

      // Prefer the first media-order visible slide (admin drag order → "mankenli"
      // first) over the variant's assigned featured image, so the gallery respects
      // the merchant's media ordering rather than jumping to a mannequin-less shot.
      var preferred = preferredSlideForVariant(variant);
      var nextSlide = firstVisible || preferred;
      if (!nextSlide) return;

      requestAnimationFrame(function () {
        if (isMobileGallery()) {
          resetGalleryDots();
          setupSlideObserver();
        }
        setActiveSlide(nextSlide);
        if (isMobileGallery()) {
          scrollToGallerySlide(nextSlide);
        } else {
          goToGallerySlide(nextSlide);
        }
      });
    });
  }

  function init() {
    initGallery();
    initVariantPicker();
    initQuantity();
    initStickyBuyBar();
  }

  // ---------- Variant picker ----------
  function initVariantPicker() {
    var jsonEl = document.querySelector('[data-product-json]');
    if (!jsonEl) return;

    var data;
    try { data = JSON.parse(jsonEl.textContent); } catch (e) { return; }
    if (!data || !data.variants) return;

    var picker = document.querySelector('[data-variant-picker]');
    var idInput = document.querySelector('[data-variant-id]');
    var priceEl = document.querySelector('[data-price]');
    var availEl = document.querySelector('[data-availability]');
    var addBtn  = document.querySelector('[data-add-to-cart]');
    var addLabel= document.querySelector('[data-add-label]');

    function normalizeOpt(val) {
      if (val == null) return '';
      var s = String(val).trim();
      try {
        if (typeof s.normalize === 'function') s = s.normalize('NFC');
      } catch (e) { /* ignore */ }
      return s.toLowerCase();
    }

    function getVariantById(id) {
      if (id == null || id === '') return null;
      var sid = String(id);
      return data.variants.find(function (v) { return String(v.id) === sid; }) || null;
    }

    function selectedOptions() {
      var arr = [];
      document.querySelectorAll('[data-option-input]').forEach(function (i) {
        var pos = parseInt(i.getAttribute('data-option-position'), 10);
        if (!pos) return;
        if (i.tagName === 'SELECT') {
          arr[pos - 1] = i.value || '';
          return;
        }
        if (!i.checked || i.disabled) return;
        arr[pos - 1] = i.getAttribute('data-option-value') || i.value;
      });
      return arr;
    }

    function firstAvailableVariant() {
      return data.variants.find(function (v) { return v.available; }) || null;
    }

    function variantIdFromLinkedOptions() {
      if (document.querySelector('select[data-option-input]')) return null;
      var checked = document.querySelectorAll('[data-option-input]:checked');
      if (!checked.length) return null;
      var lastId = null;
      var mismatch = false;
      checked.forEach(function (input) {
        var lid = input.getAttribute('data-linked-variant-id');
        if (!lid) return;
        if (lastId && lastId !== lid) mismatch = true;
        lastId = lid;
      });
      return mismatch ? null : lastId;
    }

    function findVariantByOptions(opts) {
      if (!opts || !opts.length) return null;
      var hasEmpty = opts.some(function (o) { return !o || String(o).trim() === ''; });
      if (hasEmpty) return null;
      if (data.options && opts.length < data.options.length) return null;
      var normalized = opts.map(normalizeOpt);
      return data.variants.find(function (v) {
        if (!v.options || v.options.length !== normalized.length) return false;
        return v.options.every(function (o, i) {
          return normalizeOpt(o) === normalized[i];
        });
      }) || null;
    }

    function optionIndex(match) {
      var names = data.options || [];
      for (var i = 0; i < names.length; i++) {
        var name = normalizeOpt(names[i]);
        if (match(name)) return i;
      }
      return -1;
    }

    function lensOptionIndex() {
      return optionIndex(function (name) { return name === 'cam'; });
    }

    function variantShowsPhotos(variant) {
      if (!variant) return false;
      if (variant.featured_image_id) return true;
      return variant.gallery_image_ids && variant.gallery_image_ids.length > 0;
    }

    function galleryVariantFor(variant) {
      var lensIdx = lensOptionIndex();
      var opts = variant && variant.options ? variant.options : selectedOptions();
      if (variant && variantShowsPhotos(variant) && lensIdx < 0) return variant;
      var sibling = data.variants.find(function (candidate) {
        if (!variantShowsPhotos(candidate)) return false;
        return candidate.options.every(function (value, index) {
          if (index === lensIdx) return true;
          var wanted = opts[index];
          if (!wanted) return false;
          return normalizeOpt(value) === normalizeOpt(wanted);
        });
      });
      if (sibling) return sibling;
      return variantShowsPhotos(variant) ? variant : null;
    }

    function resolveVariant() {
      var unselectedSelect = document.querySelector('select[data-option-input]');
      if (unselectedSelect && (!unselectedSelect.value || unselectedSelect.value.trim() === '')) {
        return null;
      }

      var linkedId = variantIdFromLinkedOptions();
      if (linkedId) {
        var linked = getVariantById(linkedId);
        if (linked) return linked;
      }

      var opts = selectedOptions();
      var byOpts = findVariantByOptions(opts);
      if (byOpts) return byOpts;

      var urlId = new URLSearchParams(window.location.search).get('variant');
      if (urlId) {
        var fromUrl = getVariantById(urlId);
        if (fromUrl) return fromUrl;
      }

      if (idInput && idInput.value) {
        var fromInput = getVariantById(idInput.value);
        if (fromInput) return fromInput;
      }

      if (data.selected_variant_id) {
        var fromData = getVariantById(data.selected_variant_id);
        if (fromData) return fromData;
      }

      return firstAvailableVariant();
    }

    function updateSwatchStyles() {
      document.querySelectorAll('[data-option-input]').forEach(function (i) {
        if (i.tagName === 'SELECT') return;
        var span = i.nextElementSibling;
        if (!span) return;
        var isColor = span.hasAttribute('data-color-swatch');
        if (i.checked) {
          span.style.borderColor = 'rgba(191,163,255,0.8)';
          if (isColor) {
            span.style.boxShadow = '0 0 0 2px rgba(191,163,255,0.4)';
          } else {
            span.style.background = 'rgba(191,163,255,0.15)';
          }
        } else if (isColor) {
          span.style.borderColor = 'rgba(255,255,255,0.18)';
          span.style.boxShadow = 'none';
        } else {
          span.style.borderColor = '';
          span.style.background = '';
        }
      });
      document.querySelectorAll('[data-option-display]').forEach(function (el) {
        var pos = parseInt(el.getAttribute('data-option-display'), 10);
        var input = document.querySelector('select[data-option-input][data-option-position="' + pos + '"]')
          || document.querySelector('[data-option-input][data-option-position="' + pos + '"]:checked');
        if (!input) return;
        if (input.tagName === 'SELECT' && input.selectedOptions && input.selectedOptions[0]) {
          el.textContent = input.selectedOptions[0].text;
        } else {
          el.textContent = input.value;
        }
      });
    }

    function applyVariant(variant) {
      if (!variant) {
        if (idInput) idInput.value = '';
        if (addBtn) {
          addBtn.disabled = true;
          addBtn.setAttribute('aria-disabled', 'true');
          addBtn.style.opacity = '0.4';
          addBtn.style.cursor = 'not-allowed';
        }
        var unselectedSelect = document.querySelector('select[data-option-input]');
        if (addLabel) {
          if (unselectedSelect && (!unselectedSelect.value || unselectedSelect.value.trim() === '')) {
            addLabel.textContent = 'Lütfen Cam Seçiniz';
          } else {
            addLabel.textContent = t('product_sold_out', 'Sold out');
          }
        }
        try {
          document.dispatchEvent(
            new CustomEvent('mootlu:variant-applied', { detail: { variant: null } })
          );
        } catch (e) {}
        var colorGallery = galleryVariantFor(null);
        if (colorGallery) filterThumbsByVariant(colorGallery);
        return;
      }
      if (idInput) idInput.value = String(variant.id);
      if (priceEl) {
        if (variant.price_formatted) {
          priceEl.textContent = variant.price_formatted;
        } else if (variant.price != null) {
          priceEl.textContent = formatMoneyCents(variant.price);
        }
      }
      if (variant.price != null) {
        updatePdpAdvantagesPrice(variant.price);
      }

      try {
        document.dispatchEvent(
          new CustomEvent('mootlu:variant-applied', { detail: { variant: variant } })
        );
      } catch (e) {}

      updateLowStock(variant);
      if (addBtn) {
        addBtn.disabled = !variant.available;
        addBtn.setAttribute('aria-disabled', variant.available ? 'false' : 'true');
        addBtn.style.opacity = variant.available ? '' : '0.4';
        addBtn.style.cursor  = variant.available ? '' : 'not-allowed';
      }
      if (addLabel) {
        addLabel.textContent = variant.available
          ? t('product_add', 'Add to Cart') + ' →'
          : t('product_sold_out', 'Sold out');
      }

      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      filterThumbsByVariant(galleryVariantFor(variant) || variant);
      if (variant.featured_image) {
        try {
          document.dispatchEvent(
            new CustomEvent('mootlu:gallery-active', { detail: { imageUrl: variant.featured_image } })
          );
        } catch (e) {}
      }
    }

    if (picker) {
      picker.addEventListener('change', function (e) {
        if (!e.target.matches('[data-option-input]')) return;
        updateSwatchStyles();
        applyVariant(resolveVariant());
      });
    }

    updateSwatchStyles();
    requestAnimationFrame(function () {
      applyVariant(resolveVariant());
    });
  }

  // ---------- Gallery ----------
  function initGallery() {
    var slides = document.querySelectorAll('[data-thumb]');
    if (!slides.length) return;

    slides.forEach(function (slide) {
      slide.addEventListener('click', function () {
        if (slide.hasAttribute('hidden')) return;
        goToGallerySlide(slide);
      });
    });

    var prevBtns = document.querySelectorAll('[data-pdp-prev]');
    var nextBtns = document.querySelectorAll('[data-pdp-next]');

    function activeSlideIndex(vis) {
      var active = document.querySelector('[data-thumb].is-active:not([hidden])');
      var idx = vis.indexOf(active);
      return idx >= 0 ? idx : 0;
    }

    prevBtns.forEach(function (prev) {
      prev.addEventListener('click', function () {
        var vis = visibleSlides();
        if (!vis.length) return;
        var idx = activeSlideIndex(vis);
        if (idx > 0) goToGallerySlide(vis[idx - 1]);
      });
    });

    nextBtns.forEach(function (next) {
      next.addEventListener('click', function () {
        var vis = visibleSlides();
        if (!vis.length) return;
        var idx = activeSlideIndex(vis);
        if (idx < vis.length - 1) goToGallerySlide(vis[idx + 1]);
      });
    });

    if (isMobileGallery()) {
      buildGalleryDots();
      setupSlideObserver();
    }

    var active = document.querySelector('[data-thumb].is-active:not([hidden])') || visibleSlides()[0];
    if (active) updateGalleryChrome(active);
  }

  // ---------- Quantity ----------
  function initQuantity() {
    var dec = document.querySelector('[data-qty-dec]');
    var inc = document.querySelector('[data-qty-inc]');
    var input = document.querySelector('[data-qty-input]');
    if (!input) return;
    if (dec) dec.addEventListener('click', function () {
      var v = Math.max(1, (parseInt(input.value, 10) || 1) - 1);
      input.value = v;
    });
    if (inc) inc.addEventListener('click', function () {
      var v = Math.max(1, (parseInt(input.value, 10) || 1) + 1);
      input.value = v;
    });
  }

  function initStickyBuyBar() {
    var bar = document.querySelector('[data-pdp-sticky-bar]');
    var gallery = document.querySelector('[data-product-gallery]');
    var form = document.getElementById('product-form');
    if (!bar || !gallery || !form) return;

    var stickyBtn = bar.querySelector('[data-pdp-sticky-atc]');
    var mainBtn = form.querySelector('[data-add-to-cart]');
    var stickyPrice = bar.querySelector('[data-pdp-sticky-price]');
    var stickyThumb = bar.querySelector('[data-pdp-sticky-thumb]');
    var mainPrice = document.querySelector('[data-price]');
    var mainImage = document.querySelector('[data-main-image]');

    if (stickyBtn && mainBtn) {
      stickyBtn.addEventListener('click', function () {
        if (mainBtn.disabled) return;
        mainBtn.click();
      });
    }

    var syncFromMain = function () {
      if (stickyPrice && mainPrice) stickyPrice.textContent = mainPrice.textContent || '';
      if (stickyThumb && mainImage && mainImage.getAttribute('src')) {
        stickyThumb.setAttribute('src', mainImage.getAttribute('src'));
      }
      if (stickyBtn && mainBtn) {
        stickyBtn.disabled = mainBtn.disabled;
        stickyBtn.setAttribute('aria-disabled', mainBtn.getAttribute('aria-disabled') || 'false');
      }
    };

    var syncFromVariant = function (variant) {
      if (!variant) return;
      if (stickyPrice) {
        stickyPrice.textContent = variant.price_formatted || formatMoneyCents(variant.price);
      }
      if (stickyThumb && variant.featured_image) {
        stickyThumb.setAttribute('src', variant.featured_image);
      }
      if (stickyBtn && mainBtn) {
        stickyBtn.disabled = mainBtn.disabled;
        stickyBtn.setAttribute('aria-disabled', mainBtn.getAttribute('aria-disabled') || 'false');
      }
    };

    document.addEventListener('mootlu:variant-applied', function (e) {
      if (e.detail && e.detail.variant) syncFromVariant(e.detail.variant);
    });

    document.addEventListener('mootlu:gallery-active', function (e) {
      if (stickyThumb && e.detail && e.detail.imageUrl) {
        stickyThumb.setAttribute('src', e.detail.imageUrl);
      }
    });

    if (typeof IntersectionObserver !== 'undefined') {
      var observer = new IntersectionObserver(
        function (entries) {
          var visible = entries[0] && entries[0].isIntersecting;
          bar.classList.toggle('is-visible', !visible);
          document.body.classList.toggle('pdp-sticky-bar-active', !visible);
        },
        { root: null, threshold: 0, rootMargin: '-20% 0px 0px 0px' }
      );
      observer.observe(gallery);
    }

    var picker = document.querySelector('[data-variant-picker]');
    if (picker) picker.addEventListener('change', syncFromMain);
    syncFromMain();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
